/**
 * Shared utility functions for LPR error handling and metrics
 */

/**
 * Extracts raw error details for debugging purposes
 */
export function extractLprRawError(error: unknown): string | undefined {
  try {
    if (!error) return undefined;

    // Handle string errors directly
    if (typeof error === 'string') {
      return error;
    }

    // Try to get error message from object
    if (error && typeof error === 'object' && 'message' in error) {
      const msg = (error as Record<string, unknown>).message;
      if (typeof msg === 'string') {
        return msg;
      }
    }

    // Fallback to JSON.stringify for complex error objects
    try {
      return JSON.stringify(error);
    } catch {
      // If stringify fails, return string representation
      return String(error);
    }
  } catch {
    // Ultimate fallback if anything goes wrong
    return undefined;
  }
}

/**
 * Extracts a meaningful error code from various error response formats
 */
export function extractLprErrorCode(error: unknown): string | undefined {
  try {
    if (!error || typeof error !== 'object') return undefined;

    const err = ((error as Record<string, unknown>).error ?? error) as Record<string, unknown>;

    if (typeof err.code === 'string') {
      return err.code;
    }

    return undefined;
  } catch {
    // Return undefined if any property access fails
    return undefined;
  }
}

/**
 * Parses API error responses to extract user-friendly error messages
 * Handles both single messages and arrays of messages
 */
export function parseLprApiError(error: unknown): string | undefined {
  try {
    if (!error || typeof error !== 'object') return undefined;

    const err = ((error as Record<string, unknown>).error ?? error) as Record<string, unknown>;

    // Handle single string messages - prioritize flockMessage for Flock errors
    if (typeof err.flockMessage === 'string' && err.flockMessage.trim()) {
      return err.flockMessage;
    }

    if (typeof err.message === 'string' && err.message.trim()) {
      return err.message;
    }

    if (typeof err.detail === 'string' && err.detail.trim()) {
      return err.detail;
    }

    if (typeof err.statusText === 'string' && err.statusText.trim()) {
      return err.statusText;
    }

    // Handle arrays of error messages (both strings and objects)
    if (Array.isArray(err.errors)) {
      const errorMessages: string[] = [];

      for (const item of err.errors) {
        if (typeof item === 'string') {
          errorMessages.push(item);
        } else if (
          item &&
          typeof item === 'object' &&
          typeof (item as Record<string, unknown>).message === 'string'
        ) {
          errorMessages.push((item as Record<string, unknown>).message as string);
        }
      }

      if (errorMessages.length > 0) {
        return errorMessages.join('; ');
      }
    }

    if (Array.isArray(err.messages)) {
      const errorMessages = err.messages.filter((msg: unknown) => typeof msg === 'string');
      if (errorMessages.length > 0) {
        return errorMessages.join('; ');
      }
    }

    return undefined;
  } catch {
    // Return undefined if any operation fails
    return undefined;
  }
}
